import { useQuery } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { GET_TRANSACTIONS_OF_VENDOR_SETTLEMENT } from "../../../Qurries";
import { toast } from "react-toastify";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { Table, RowsPerPageSelect } from "../../../components/Table";
import { amountFormat } from "../../../utils/amountFormat";

function TransactionsOfSettlement() {
    const { state } = useLocation();

    const [cursorStack, setCursorStack] = useState<string[]>([]);

    const [itemsPerRow, setItemsPerRow] = useState({ name: 10 });
    const [skip, setSkip] = useState(0);
    const [refetchLoading, setRefetchLoading] = useState(false);

    const { data, loading, error, refetch } = useQuery(
        GET_TRANSACTIONS_OF_VENDOR_SETTLEMENT,
        {
            variables: {
                utr: state?.utrno,
                cursor: "",
                limit: itemsPerRow.name,
                skip,
            },
            notifyOnNetworkStatusChange: true,
        }
    );

    useEffect(() => {
        if (error) {
            toast.error(error.message || "Failed to fetch transactions");
        }
    }, [error]);

    const getSerialNumber = (index: number) => {
        return cursorStack.length * itemsPerRow.name + skip + index + 1;
    };

    const isCursorPagination =
        data?.getMerchantVendorSettlementsTransactions?.cursor &&
        data?.getMerchantVendorSettlementsTransactions?.cursor !== "N/A";

    const isNextDisabled = () => {
        const response = data?.getMerchantVendorSettlementsTransactions;
        if (!response) return true;
        const { cursor, hasMore, settlements_transactions } = response;
        if (cursor === "N/A") {
            if (hasMore !== undefined && hasMore !== null) {
                return !hasMore;
            }
            return (
                settlements_transactions.length < itemsPerRow.name
            );
        }
        if (hasMore !== undefined && hasMore !== null) {
            return !hasMore;
        }
        return cursor === null || cursor === "N/A";
    };

    return (
        <div className="min-h-[80vh] rounded-lg mt-4 bg-[#F6F8FA] p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="flex items-center text-lg font-semibold text-[#1B163B]">
                    <Link to={state?.from ?? "/payments/vendor/settlements"}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className="h-6 mr-2"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15.75 19.5 8.25 12l7.5-7.5"
                            />
                        </svg>
                    </Link>
                    Go Back
                </h3>
            </div>

            {loading || refetchLoading ? (
                <div className="min-h-[60vh] flex justify-center items-center">
                    <AiOutlineLoading3Quarters className="animate-spin text-xl" />
                </div>
            ) : (
                <Table
                    perPage={false}
                    heading="Transactions Of Settlement"
                    exportBtn
                    searchBox={
                        <RowsPerPageSelect
                            setItemsPerRow={setItemsPerRow}
                            itemsPerRow={itemsPerRow}
                        />
                    }
                    data={[
                        [
                            "Sr.No",
                            "Date & Time",
                            "Order ID",
                            "Payment ID",
                            "Order Amt",
                            "Transaction Amt",
                            "Vendor Order Amt",
                            "Payment Method",
                            "Status",
                            "Student Name",
                            "Student ID",
                            "Phone No.",
                            "Settlement Date",
                        ],
                        ...(data?.getMerchantVendorSettlementsTransactions?.settlements_transactions?.map(
                            (t: any, index: number) => [
                                getSerialNumber(index),
                                new Date(t.event_time).toLocaleString("hi"),
                                t.custom_order_id || t.order_id || "N/A",
                                t.payment_id || "N/A",
                                amountFormat(t.order_amount),
                                amountFormat(t.event_amount),
                                amountFormat(state.vendor_transaction_amount),
                                t.payment_group || "N/A",
                                <span
                                    className={
                                        t.event_status?.toLowerCase() === "success"
                                            ? "text-green-600"
                                            : ""
                                    }
                                >
                                    {t.event_status}
                                </span>,
                                t.student_name || "N/A",
                                t.student_id || "N/A",
                                t.student_phone_no || "N/A",
                                new Date(state.settlementDate).toLocaleString("hi"),
                            ]
                        ) || []),
                    ]}
                    footer={
                        <div className="flex justify-between px-4 my-4">
                            {/* PREV */}
                            <button
                                disabled={cursorStack.length === 0 && skip === 0}
                                onClick={async () => {
                                    setRefetchLoading(true);

                                    if (cursorStack.length > 0) {
                                        const prevCursor =
                                            cursorStack.length > 1
                                                ? cursorStack[cursorStack.length - 2]
                                                : "";

                                        setCursorStack((prev) => prev.slice(0, -1));

                                        await refetch({
                                            utr: state?.utrno,
                                            cursor: prevCursor,
                                            limit: itemsPerRow.name,
                                        });
                                    } else {
                                        const updatedSkip = Math.max(0, skip - itemsPerRow.name);
                                        setSkip(updatedSkip);

                                        await refetch({
                                            utr: state?.utrno,
                                            skip: updatedSkip,
                                            limit: itemsPerRow.name,
                                        });
                                    }

                                    setRefetchLoading(false);
                                }}
                                className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 text-white disabled:bg-gray-400"
                            >
                                Prev
                            </button>

                            {/* NEXT */}
                            <button
                                disabled={isNextDisabled()}
                                onClick={async () => {
                                    setRefetchLoading(true);

                                    if (isCursorPagination) {
                                        const newCursor =
                                            data.getMerchantVendorSettlementsTransactions.cursor;

                                        setCursorStack((prev) => [...prev, newCursor]);

                                        await refetch({
                                            utr: state?.utrno,
                                            cursor: newCursor,
                                            limit: itemsPerRow.name,
                                        });
                                    } else {
                                        const updatedSkip = skip + itemsPerRow.name;
                                        setSkip(updatedSkip);

                                        await refetch({
                                            utr: state?.utrno,
                                            skip: updatedSkip,
                                            limit: itemsPerRow.name,
                                        });
                                    }

                                    setRefetchLoading(false);
                                }}
                                className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 text-white disabled:bg-gray-400"
                            >
                                Next
                            </button>
                        </div>
                    }
                />
            )}
        </div>
    );
}

export default TransactionsOfSettlement;
